<template>
  <div class="offcanvas offcanvas-end" tabindex="-1" :id="offcanvas_id" data-bs-backdrop="static" :aria-labelledby="`${offcanvas_id}_label`">
    <div class="offcanvas-header">
      <button class="btn-back" :id="`btnClose${this.offcanvas_id}`" type="button" name="button" data-test="btnCloseOffcanvas" data-bs-dismiss="offcanvas" @click="doCancel" aria-label="Close">
        <IconArrowBack/>
        {{ $t('goBack') }}
      </button>
      <h5 class="offcanvas-title d-block" :id="`${offcanvas_id}_label`">
        {{ $t('patientSearch.searchingFor') }}
        <strong>{{ searchValue }}</strong>
      </h5>
    </div>

    <div class="offcanvas-body">
      <p>{{ $t('patientSearch.selectThePatient') }}</p>

      <div class="no-results-box" v-if="alert.message">
        <BaseNoResults :title="alert.title" :text="alert.message"/>
      </div>

      <ul class="scrollbar" v-else>
        <li v-for="(item, index) in patientList" :key="index" @click="doConfirm(item)">
          <span>{{ item.fullName }}</span>

          <small>{{ formatDate(item.lastExamDate) }}</small>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.offcanvas {
  .offcanvas-title {
    font-size:                          var(--font-size-22);

    strong {
      font-size:                        38px;
      display:                          block;
      font-weight:                      700;
      text-transform:                   capitalize;
    }
  }

  .offcanvas-body {
    display:                            flex;
    flex-direction:                     column;
    justify-content:                    flex-start;
    gap:                                26px;

    p {
      color:                            var(--Brand-Primary);
      font-size:                        var(--font-size-14);
      font-weight:                      300;
      line-height:                      140%; /* 19.6px */
      margin-bottom:                    0;
    }

    ul {
      display:                          block;
      max-height:                       calc(100vh - 200px);
      margin-bottom:                    0;
      padding:                          0 10px 0 0;

      li {
        @include                        border_radius(10px);
        display:                        flex;
        justify-content:                space-between;
        align-items:                    center;
        gap:                            10px;
        margin-bottom:                  10px;
        padding:                        10px;
        background:                     var(--Shades-10);
        border:                         1px solid var(--Shades-20);
        cursor:                         pointer;

        &:last-child {
          margin:                       0;
        }

        span {
          color:                        var(--Brand-Primary);
          font-size:                    var(--font-size-16);
          font-weight:                  400;
          line-height:                  normal;
          text-overflow:                ellipsis;
          overflow:                     hidden;
        }

        small {
          color:                        var(--Shades-70);
          font-size:                    var(--font-size-10);
          font-weight:                  700;
          line-height:                  normal;
          letter-spacing:               0.5px;
        }
      }
    }
  }
}

:deep(.no-results-main) {
  text-align:                          center;
}
</style>

<script>
export default {
  name: 'ExamListSearchedPatientsOffcanvas',
  props: ['offcanvas_id'],
  data: () => ({
    patientList: [],
    searchValue: null,
    selectedPatient: {},
    offcanvas_list_searched_patients: null,
    resolvePromise: null,
    rejectPromise: null,
    alert: {
      title: null,
      message: null,
      class: null
    },
  }),
  mounted() {
    const offcanvasElement = document.getElementById(this.offcanvas_id)

    if (offcanvasElement) {
      this.offcanvas_list_searched_patients = new bootstrap.Offcanvas(offcanvasElement)
      offcanvasElement.addEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
    }
  },
  methods: {
    show(options = {}) {
      if (options.patientName)
        this.searchValue = options.patientName

      this.doSearch(this.searchValue);

      this.$nextTick(() => {
        if (this.offcanvas_list_searched_patients) {
          this.offcanvas_list_searched_patients.show()
        } else {
          const offcanvasElement = document.getElementById(this.offcanvas_id)
          this.offcanvas_list_searched_patients = new bootstrap.Offcanvas(offcanvasElement)
          this.offcanvas_list_searched_patients.show()
          offcanvasElement.addEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
        }
      });

      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },

    doConfirm(patient) {
      if (this.offcanvas_list_searched_patients) {
        this.offcanvas_list_searched_patients.hide()
      }

      this.resolvePromise(patient)
    },

    doCancel() {
      if (this.offcanvas_list_searched_patients) {
        this.offcanvas_list_searched_patients.hide()
      }

      this.resolvePromise(false)
    },

    async doSearch(partialName) {
      this.alert = {}
      const params = { partialName }
      this.loading = true
      NProgress.start()

      try {
        const response = await this.$patientService.searchPatient(params);

        this.patientList = Array.isArray(response.result) ? response.result : [];

        if (response.result.length === 0) {
          this.alert = this.$message.alertWithTitle(this, 'request_notfound_title', 'request_notfound_text')
        }
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error');
        this.patientList = []
      } finally {
        NProgress.done(true);
        this.loading = false;
      }
    },

    formatDate(date) {
      return moment(date).format(this.$i18n.t('dateTimeFormat'))
    },

    handleHiddenEvent() {
      if (this.resolvePromise && this.offcanvas_list_searched_patients._isShown) {
        this.resolvePromise(false)
      } else if (this.resolvePromise) {
        this.resolvePromise(false)
      }

      this.resetState()
      this.offcanvas_list_searched_patients = null
    },

    resetState() {
      this.patientList = []
      this.searchValue = null
      this.selectedPatient = {}
    },
  },
  beforeUnmount() {
    const offcanvasElement = document.getElementById(this.offcanvas_id)
    if (offcanvasElement) {
      offcanvasElement.removeEventListener('hidden.bs.offcanvas', this.handleHiddenEvent)
    }
  },
}
</script>
